/* import __COLOCATED_TEMPLATE__ from './saml-settings.hbs'; */
/* RESPONSIBLE TEAM: team-app-security */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { UNLIMITED_USAGE } from 'embercom/lib/settings/copilot-access-editor/constants';
import { run } from '@ember/runloop';
import {
  FULL_ACCESS_ROLE_NAME,
  NO_ACCESS_ROLE_NAME,
  VIEW_ONLY_ROLE_NAME,
} from 'embercom/components/table-cells/teammate-list/standalone-access-level-selector';

export default class SamlSettings extends Component {
  @service appService;
  @service notificationsService;
  @service store;
  @service intl;
  @service copilotDataService;
  @service permissionsHierarchyService;

  @tracked idpSsoTargetUrl = this.args.samlAccount.idpSsoTargetUrl?.replace('https://', '');

  @tracked shouldShowPermissionsModal = false;
  @tracked permissionsObject = this._getDefaultPermissionsObject();

  // Standalone-specific tracked properties
  @tracked selectedStandaloneRoleId = this._getInitialStandaloneRoleId();
  @tracked copilotUsage = this._getInitialCopilotUsage();
  @tracked isLoadingCopilotData = false;

  _getDefaultPermissionsObject() {
    return this._getPermissionsObject(this.args.samlAccount.jitSettings);
  }

  _getPermissionsObject(permissions) {
    permissions = this.args.samlAccount.jitSettings || {};
    let permissionsObject = this.store.createRecord('jit-settings-permissions', permissions);
    return permissionsObject;
  }

  get canEnableJustInTimeProvisioning() {
    return !this.appService.app.is_test_app;
  }

  get disabledCopilotOptions() {
    return [UNLIMITED_USAGE];
  }

  get isStandalone() {
    return this.appService.app.canUseStandalone;
  }

  get standaloneRoles() {
    return this.store.peekAll('role').toArray();
  }

  get sortedStandaloneRoles() {
    // Sort roles: Full Access first, then View Only, then No Access
    let roleOrder = {
      [FULL_ACCESS_ROLE_NAME]: 0,
      [VIEW_ONLY_ROLE_NAME]: 1,
      [NO_ACCESS_ROLE_NAME]: 2,
    };
    return this.standaloneRoles.sort((a, b) => {
      return (roleOrder[a.name] ?? 99) - (roleOrder[b.name] ?? 99);
    });
  }

  get standaloneWorkspaceRoles() {
    // Show No Access role only when copilot access is enabled (unlimited)
    // (No Access means they have copilot seat but no workspace access)
    if (this.copilotUsage === 'unlimited') {
      return this.sortedStandaloneRoles;
    }
    // Otherwise, filter out No Access role
    return this.sortedStandaloneRoles.filter((role) => role.name !== NO_ACCESS_ROLE_NAME);
  }

  get selectedStandaloneRole() {
    return this.standaloneRoles.find((role) => role.id === this.selectedStandaloneRoleId);
  }

  get fullAccessRole() {
    return this.standaloneRoles.find((role) => role.name === FULL_ACCESS_ROLE_NAME);
  }

  get noAccessRole() {
    return this.standaloneRoles.find((role) => role.name === NO_ACCESS_ROLE_NAME);
  }

  get copilotSeatPrice() {
    let priceInCents = this.copilotDataService.copilotData?.price || 0;
    return this.intl.formatNumber(priceInCents / 100, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    });
  }

  _getInitialStandaloneRoleId() {
    let jitSettings = this.args.samlAccount.jitSettings;
    // If role_id is set, use it; otherwise default to full access role
    if (jitSettings?.standalone_role_id) {
      return jitSettings.standalone_role_id;
    }
    // For backwards compatibility, if roles are loaded, return full access role id
    let fullAccessRole = this.store
      .peekAll('role')
      .find((role) => role.name === FULL_ACCESS_ROLE_NAME);
    return fullAccessRole?.id || null;
  }

  _getInitialCopilotUsage() {
    let jitSettings = this.args.samlAccount.jitSettings;
    // If copilot_usage is set, use it; otherwise default to 'off'
    if (jitSettings?.copilot_usage !== undefined) {
      return jitSettings.copilot_usage;
    }
    return 'off';
  }

  @action
  updateIdpSsoTargetUrl() {
    // We need to run this in a runloop because the the on modifier does not schedule the event listener onto the run loop
    // https://github.com/emberjs/ember.js/issues/19222#issuecomment-1049773929
    run(() => {
      let targetUrl = this.idpSsoTargetUrl.trim().replace('https://', '') || '';
      this.idpSsoTargetUrl = targetUrl;
      this.args.samlAccount.idpSsoTargetUrl = `https://${targetUrl}`;
    });
  }

  @action
  updateIdpSsoTargetUrlOnPaste(event) {
    // We need to run this in a runloop because the the on modifier does not schedule the event listener onto the run loop
    // https://github.com/emberjs/ember.js/issues/19222#issuecomment-1049773929
    run(() => {
      let targetUrl = (event.clipboardData || window.clipboardData).getData('text');
      targetUrl = targetUrl.trim().replace('https://', '') || '';
      this.idpSsoTargetUrl = targetUrl;
      this.args.samlAccount.idpSsoTargetUrl = `https://${targetUrl}`;
    });
  }

  @action
  openPermissionsModal() {
    // Reset standalone state when opening modal
    if (this.isStandalone) {
      this.selectedStandaloneRoleId = this._getInitialStandaloneRoleId();
      this.copilotUsage = this._getInitialCopilotUsage();
      // Load copilot data lazily for standalone apps (don't block modal opening)
      this._loadCopilotDataAsync();
    }
    this.shouldShowPermissionsModal = true;
  }

  async _loadCopilotDataAsync() {
    this.isLoadingCopilotData = true;
    await this.copilotDataService.ensureSetup();
    this.isLoadingCopilotData = false;
  }

  @action
  onStandaloneRoleChange(roleId) {
    this.selectedStandaloneRoleId = roleId;
  }

  @action
  onCopilotUsageChange(value) {
    // If turning off copilot access and currently selected role is "No Access",
    // switch to Full Access (since No Access requires copilot)
    if (value === 'off' && this.selectedStandaloneRoleId === this.noAccessRole?.id) {
      this.selectedStandaloneRoleId = this.fullAccessRole?.id;
    }
    this.copilotUsage = value;
  }

  @action
  updatePermissions() {
    try {
      if (this.isStandalone) {
        this.args.samlAccount.jitSettings = {
          standalone_role_id: this.selectedStandaloneRoleId,
          copilot_usage: this.copilotUsage,
        };
      } else {
        this.permissionsObject.validate();
        this.args.samlAccount.jitSettings = {
          ...this.permissionsObject.permissions(),
          ...this.permissionsObject.conversationAccess,
        };
      }
      this.shouldShowPermissionsModal = false;
    } catch (error) {
      this.notificationsService.notifyError(error.message);
    }
  }

  @action
  closePermissionsModalAndRollback() {
    this.permissionsObject = this._getPermissionsObject(this.args.samlAccount.jitSettings);
    // Reset standalone state
    if (this.isStandalone) {
      this.selectedStandaloneRoleId = this._getInitialStandaloneRoleId();
      this.copilotUsage = this._getInitialCopilotUsage();
    }
    this.shouldShowPermissionsModal = false;
  }
}
